import React, { useEffect, useState } from "react";
import { Layout, PageHeader } from "@/components/Layout";
import { History, Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";

const ROLE_COLORS = {
  director: "bg-violet-100 text-violet-700",
  manager: "bg-violet-100 text-violet-700",
  admin: "bg-violet-100 text-violet-700",
  clerk: "bg-amber-100 text-amber-700",
  branch_manager: "bg-sky-100 text-sky-700",
};

function fmt(at) {
  if (!at) return "—";
  const d = new Date(at);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function ActivityLog() {
  const { branches } = useBranch();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [branch, setBranch] = useState("all");
  const [userEmail, setUserEmail] = useState("all");
  const [fromDate, setFromDate] = useState(isoDaysAgo(30));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : (r.data?.users || []))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/activity", { params: { branch, user: userEmail, from_date: fromDate, to_date: toDate } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [branch, userEmail, fromDate, toDate]);

  const exportCsv = () => {
    const qs = new URLSearchParams({ branch, user: userEmail, from_date: fromDate, to_date: toDate }).toString();
    window.open(`${API}/activity/export?${qs}`, "_blank");
  };

  return (
    <Layout>
      <PageHeader title="Activity Log" subtitle="Every change made across all branches" />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Branch</label>
          <select data-testid="activity-branch-filter" value={branch} onChange={(e) => setBranch(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium focus:ring-2 focus:ring-slate-900 outline-none">
            <option value="all">All Branches</option>
            {branches.map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">User</label>
          <select data-testid="activity-user-filter" value={userEmail} onChange={(e) => setUserEmail(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium focus:ring-2 focus:ring-slate-900 outline-none">
            <option value="all">All Users</option>
            {users.map((u) => <option key={u.email} value={u.email}>{u.email}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">From</label>
          <input type="date" data-testid="activity-from-date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">To</label>
          <input type="date" data-testid="activity-to-date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
        </div>
        <button data-testid="activity-export-btn" onClick={exportCsv}
          className="ml-auto bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-700 flex items-center gap-2">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="activity-table">
        {loading ? (
          <div className="p-8 text-slate-400 animate-pulse text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <History className="h-8 w-8 text-slate-300" />
            No activity recorded for this filter yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-semibold">When</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Branch</th>
                <th className="px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.at}-${r.userEmail}-${i}`} data-testid="activity-row" className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap font-mono-fig text-xs">{fmt(r.at)}</td>
                  <td className="px-4 py-3 text-slate-800">{r.userName || r.userEmail}<div className="text-[11px] text-slate-400">{r.userEmail}</div></td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold rounded px-1.5 py-0.5 ${ROLE_COLORS[r.role] || "bg-slate-100 text-slate-600"}`}>{r.role}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.branch}</td>
                  <td className="px-4 py-3 text-slate-800">{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
